import { CalendarRef, ConnectionCalendar } from '@griply/types';
import { getConnectionCalendar } from '@griply/utils';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarColorBar } from 'shared/components/ui/calendar-color-bar';
import {
  CustomFormSelect,
  Option,
  Size as SelectSize,
} from 'shared/components/ui/custom-form-select';

import * as Styled from './calendar-select.style';

const toOptionValue = ({ connectionId, id }: ConnectionCalendar) =>
  `${connectionId}:${id}`;

export type CalendarOptionValueProps = { value: Option };

const CalendarOptionValue: React.FC<CalendarOptionValueProps> = ({ value }) => (
  <Styled.SelectValue>
    {value.start}
    <Styled.SelectLabel>{value.label}</Styled.SelectLabel>
  </Styled.SelectValue>
);

export type CalendarSelectProps = {
  // Every known calendar — resolves the current value even when its connection can no longer sync.
  calendars: ConnectionCalendar[];
  selectableCalendars: ConnectionCalendar[];
  value: CalendarRef | null;
  isLoading?: boolean;
  onChange: (value: CalendarRef) => void;
};

export const CalendarSelect: React.FC<CalendarSelectProps> = ({
  calendars,
  selectableCalendars,
  value,
  isLoading,
  onChange,
}) => {
  const { t } = useTranslation();

  const selected = useMemo(
    () => getConnectionCalendar(calendars, value),
    [calendars, value],
  );

  const options = useMemo(
    () =>
      selectableCalendars.map((calendar) => ({
        value: toOptionValue(calendar),
        label: calendar.name,
        start: <CalendarColorBar color={calendar.color} />,
      })),
    [selectableCalendars],
  );

  const onSelect = (optionValue?: string) => {
    const calendar = selectableCalendars.find(
      (calendar) => toOptionValue(calendar) === optionValue,
    );
    if (!calendar) {
      return;
    }

    onChange({ connectionId: calendar.connectionId, calendarId: calendar.id });
  };

  // While the calendars load nothing resolves, so the placeholder slot carries the loading label.
  const placeholder = isLoading
    ? t('general.labels.loading')
    : t('calendar.sync-tasks.placeholder');

  const selectedValue = selected ? toOptionValue(selected) : undefined;
  // a calendar missing from the options (connection can no longer sync) keeps its name visible via the trigger label
  const unselectableLabel = options.some(
    (option) => option.value === selectedValue,
  )
    ? undefined
    : selected?.name;

  return (
    <CustomFormSelect
      options={options}
      value={selectedValue}
      selectedLabel={unselectableLabel}
      renderValue={CalendarOptionValue}
      placeholder={placeholder}
      onChange={onSelect}
      size={SelectSize.ExtraSmall}
      noOverflow
      disabled={isLoading}
    />
  );
};
